/**
 * The audit-action catalog (docs/01 §7; docs/11 F-1).
 *
 * `audit_events.action` is a machine key. It was also, until this file existed,
 * what the screen showed: the UI carried a hand-written map of 20 labels while
 * the API wrote 78 distinct actions, so 58 of them rendered as
 * `vendor_contact.unassigned` — and the admin ledger rendered raw keys for ALL
 * of them, in its rows and in its filter dropdown.
 *
 * A longer map would have the same failure a release later, so the catalog is
 * EXHAUSTIVE and `scripts/ci/check-audit-catalog.ts` enforces it: an action the
 * API writes with no entry here fails the build, and an entry here that nothing
 * writes fails it too. Adding an audited action now costs one line, and
 * forgetting it costs a red build instead of a raw enum on a customer's screen.
 *
 * `category` exists because a filter offering 78 keys is not a filter. It is
 * the grouping the history tab and the admin ledger both filter by.
 */

import { readableFallback } from './vocabulary.ts';

/** The filterable groupings. Ordered as the filter renders them. */
export const AUDIT_CATEGORIES = [
  'record',
  'people',
  'documents',
  'notes',
  'access',
  'security',
  'reference',
  'bulk',
] as const;
export type AuditCategory = (typeof AUDIT_CATEGORIES)[number];

export const AUDIT_CATEGORY_LABEL: Record<AuditCategory, string> = {
  record: 'Record changes',
  people: 'People & assignments',
  documents: 'Documents',
  notes: 'Notes',
  access: 'Access & permissions',
  security: 'Security codes',
  reference: 'Reference data',
  bulk: 'Bulk operations',
};

export interface AuditActionDefinition {
  /** What happened, in the reader's words. Names its subject: a feed mixes them. */
  readonly label: string;
  readonly category: AuditCategory;
}

export const AUDIT_ACTIONS = {
  // ---- Bulk runs (0071; docs/01 §7; ADR-67) --------------------------------
  // The BATCH-level event — Matt's "global history log with appropriate
  // context". The per-record entries a run writes keep their own module's
  // action and category, because a released action item is a change to that
  // action item whatever caused it; this is the row that says what the RUN did.
  //
  // Its own category rather than squeezing into `record`, because "which bulk
  // runs happened" is a question no existing grouping answers. That it is
  // currently the only member costs nothing: the history filter already refuses
  // to render a category grouping fewer than two actions present, so the
  // grouping appears the day a second one exists and not before.
  'bulk_batch.completed': { label: 'Bulk run completed', category: 'bulk' },

  // ---- Addresses and phone numbers (0040; docs/02 the two standards) ------
  // Category `record`: changing where a company's mail goes is a change to the
  // company, and a reader looking at its history wants it in the same list.
  'address.created': { label: 'Address added', category: 'record' },
  'address.updated': { label: 'Address updated', category: 'record' },
  'address.inactivated': { label: 'Address retired', category: 'record' },
  'phone_number.created': { label: 'Phone number added', category: 'record' },
  'phone_number.updated': { label: 'Phone number updated', category: 'record' },
  'phone_number.inactivated': { label: 'Phone number retired', category: 'record' },

  // ---- Properties, spaces, groups -----------------------------------------
  'property.created': { label: 'Property created', category: 'record' },
  'property.updated': { label: 'Property updated', category: 'record' },
  'property.inactivated': { label: 'Property inactivated', category: 'record' },
  'property.reactivated': { label: 'Property reactivated', category: 'record' },
  'property.photo_changed': { label: 'Property photo changed', category: 'record' },
  'property.photo_removed': { label: 'Property photo removed', category: 'record' },
  // 4.3 — the framing changed, not the photo. A separate action because
  // "changed" and "reframed" are different things to a reader of the history.
  'property.photo_cropped': { label: 'Property photo re-cropped', category: 'record' },
  'property.people_changed': { label: 'Property people changed', category: 'record' },
  'space.created': { label: 'Suite added', category: 'record' },
  'space.updated': { label: 'Suite corrected', category: 'record' },
  'space.inactivated': { label: 'Suite inactivated', category: 'record' },
  'space.reactivated': { label: 'Suite reactivated', category: 'record' },
  'group.created': { label: 'Group created', category: 'record' },
  'group.updated': { label: 'Group updated', category: 'record' },

  // ---- Tenants ------------------------------------------------------------
  'tenant_org.created': { label: 'Tenant organization created', category: 'record' },
  'tenant_org.updated': { label: 'Tenant organization updated', category: 'record' },
  'tenant_org.inactivated': { label: 'Tenant organization inactivated', category: 'record' },
  'tenant_org.reactivated': { label: 'Tenant organization reactivated', category: 'record' },
  'tenancy.created': { label: 'Tenancy created', category: 'record' },
  'tenancy.updated': { label: 'Tenancy updated', category: 'record' },
  'tenancy.inactivated': { label: 'Tenancy ended', category: 'record' },
  'tenancy.reactivated': { label: 'Tenancy reinstated', category: 'record' },
  'lease.created': { label: 'Lease added', category: 'record' },
  'lease.updated': { label: 'Lease updated', category: 'record' },
  'lease.activated': { label: 'Lease activated', category: 'record' },
  'lease.superseded': { label: 'Lease superseded', category: 'record' },
  'lease_abstract.revised': { label: 'Lease abstract revised', category: 'record' },
  // 0069. 'record' rather than 'people': the diff is a change to what the
  // LEASE says, not to anybody's access — the people named are the tenant's
  // contacts, and their own records are untouched by it.
  'lease_contacts.set': { label: 'Lease contacts changed', category: 'record' },
  // Key dates (0045). 'record' rather than 'notes': a key date is a change to
  // what the record OWES, and its progress notes are ordinary notes filed
  // against it.
  'key_date.created': { label: 'Key date added', category: 'record' },
  'key_date.updated': { label: 'Key date changed', category: 'record' },
  'key_date.completed': { label: 'Key date completed', category: 'record' },
  'key_date.cancelled': { label: 'Key date canceled', category: 'record' },
  'key_date.reopened': { label: 'Key date reopened', category: 'record' },
  // 0047 ownership. 'people' rather than 'record': these answer "who is doing
  // this", which is the question the People & assignments filter exists for.
  'key_date.claimed': { label: 'Key date claimed', category: 'people' },
  'key_date.assigned': { label: 'Key date assigned to someone', category: 'people' },
  'key_date.released': { label: 'Key date ownership released', category: 'people' },
  'tenant_contact.created': { label: 'Tenant contact added', category: 'people' },
  'tenant_contact.updated': { label: 'Tenant contact edited', category: 'people' },
  'tenant_contact.inactivated': { label: 'Tenant contact retired', category: 'people' },
  'tenant_contact.reactivated': { label: 'Tenant contact restored', category: 'people' },

  // ---- Vendors, agreements, utility accounts ------------------------------
  'vendor.created': { label: 'Vendor created', category: 'record' },
  // Its OWN event, not a field inside `vendor.created` (RM-038). Somebody
  // reviewing duplicate hygiene is asking "which vendors were created past a
  // warning, and what was the stated reason" — a question a filterable action
  // answers and a nested snapshot key does not. It is written IN ADDITION to
  // `vendor.created`, which stays the record's creation event; this one carries
  // only the override.
  'vendor.created_despite_duplicates': {
    label: 'Vendor created despite a duplicate warning',
    category: 'record',
  },
  // The ORCHESTRATION event for the atomic utility save (punch 5.2). It does
  // not replace `vendor.created`, `utility_account.created` or
  // `vendor_property.linked`-equivalents — each record still writes its own
  // history, or the account's ledger would have a hole where its creation
  // should be. This one exists to say the three were one save, and where the
  // user was standing when they made it.
  'vendor.created_with_account': {
    label: 'Utility vendor created with its first account',
    category: 'record',
  },
  'vendor.updated': { label: 'Vendor updated', category: 'record' },
  'vendor.inactivated': { label: 'Vendor inactivated', category: 'record' },
  'vendor.reactivated': { label: 'Vendor reactivated', category: 'record' },
  'vendor.services_changed': { label: "Vendor's services changed", category: 'record' },
  'vendor.utility_types_changed': {
    label: "Vendor's utility types changed",
    category: 'record',
  },
  'vendor_service.added': { label: 'Service added to vendor', category: 'record' },
  'vendor_utility_type.added': { label: 'Utility type added to vendor', category: 'record' },
  'vendor_property.linked': { label: 'Property linked to vendor', category: 'record' },
  'vendor_property.updated': { label: 'Vendor–property link updated', category: 'record' },
  'vendor_property.inactivated': { label: 'Property unlinked from vendor', category: 'record' },
  'vendor_property.reactivated': { label: 'Property link restored', category: 'record' },
  'vendor_property.services_changed': {
    label: 'Services at this property changed',
    category: 'record',
  },
  'vendor_contact.created': { label: 'Vendor contact added', category: 'people' },
  'vendor_contact.updated': { label: 'Vendor contact updated', category: 'people' },
  'vendor_contact.inactivated': { label: 'Vendor contact inactivated', category: 'people' },
  'vendor_contact.reactivated': { label: 'Vendor contact reactivated', category: 'people' },
  'vendor_contact.assigned': { label: 'Contact assigned to a service', category: 'people' },
  'vendor_contact.unassigned': { label: 'Contact assignment removed', category: 'people' },
  'agreement.created': { label: 'Agreement drafted', category: 'record' },
  'agreement.updated': { label: 'Agreement updated', category: 'record' },
  'agreement.activated': { label: 'Agreement activated', category: 'record' },
  'agreement.superseded': { label: 'Agreement superseded', category: 'record' },
  'agreement.ended': { label: 'Agreement ended', category: 'record' },
  'agreement.discarded': { label: 'Draft agreement discarded', category: 'record' },
  'utility_account.created': { label: 'Utility account added', category: 'record' },
  'utility_account.updated': { label: 'Utility account updated', category: 'record' },
  'utility_account.inactivated': { label: 'Utility account retired', category: 'record' },
  'utility_account.reactivated': { label: 'Utility account restored', category: 'record' },
  // Split out of `utility_account.updated`'s field diff: which BUILDINGS an
  // account serves is the fact the property side reads, and burying it in a
  // generic update event meant "when did this meter stop covering Building C"
  // could only be answered by opening every update in the ledger.
  'utility_account.properties_changed': {
    label: 'Properties served by this account changed',
    category: 'record',
  },
  // Who may call the utility about this meter. Filed under `access` rather than
  // `record`: an access review asks who WAS authorised, and that question wants
  // these two next to the codes-vault reveals, not next to a cost change.
  'utility_account.authorized_user_added': {
    label: 'Authorized user added to utility account',
    category: 'access',
  },
  'utility_account.authorized_user_removed': {
    label: 'Authorized user removed from utility account',
    category: 'access',
  },
  'utility_account.authorized_user_renamed': {
    label: 'Authorized user’s name capitalized',
    category: 'record',
  },

  // ---- Management agreements (TKC's own contracts) ------------------------
  'management_agreement.created': { label: 'Management agreement added', category: 'record' },
  'management_agreement.updated': { label: 'Management agreement updated', category: 'record' },
  'management_agreement.ended': { label: 'Management agreement ended', category: 'record' },
  // Two events, not one: each answers a question asked from a different page —
  // "what replaced this" on the predecessor, "what did this replace" on the
  // successor (0055).
  'management_agreement.superseded': {
    label: 'Management agreement superseded',
    category: 'record',
  },
  'management_agreement.supersedes': {
    label: 'Management agreement replaced an earlier one',
    category: 'record',
  },

  // ---- Documents ----------------------------------------------------------
  'document.created': { label: 'Document added', category: 'documents' },
  'document.updated': { label: 'Document updated', category: 'documents' },
  /**
   * Punch 6.1a. Its OWN action rather than a line among the field diffs of
   * `document.updated`: "who can change this document" is the question an
   * administrator searches the ledger for, and it would be unfindable buried
   * among title edits.
   */
  'document.access_mode_changed': {
    label: 'Document access changed',
    category: 'documents',
  },
  'document.verify_status_changed': { label: 'Document link checked', category: 'documents' },
  'document_association.created': { label: 'Document linked to a record', category: 'documents' },
  'document_association.inactivated': { label: 'Document unlinked', category: 'documents' },
  'document_association.superseded': { label: 'Document replaced', category: 'documents' },

  // ---- Notes --------------------------------------------------------------
  'note.created': { label: 'Note added', category: 'notes' },
  'note.updated': { label: 'Note edited', category: 'notes' },
  'note.inactivated': { label: 'Note made inactive', category: 'notes' },
  'note.reactivated': { label: 'Note made active', category: 'notes' },
  // Punch item 3.1. A question and its answer are separate events on purpose:
  // "who asked" and "who answered" are the two things a thread is read for.
  'note.ask_resolved': { label: 'Question answered', category: 'notes' },
  'note.ask_reopened': { label: 'Question reopened', category: 'notes' },
  // Punch item 3.2. Its own event because "who was pulled into this, and when"
  // is a question the message body cannot answer after an edit.
  'note.mentioned': { label: 'People mentioned', category: 'notes' },

  // ---- Insights (punch 6.1b) ---------------------------------------------
  //
  // Both events are PER USER and per OCCURRENCE. They are audited because
  // "everyone dismissed this and nobody acted on it" is a question worth being
  // able to answer, and because an acknowledgement is the one act in this
  // feature that changes what a person will be shown.
  'insight.acknowledged': { label: 'Insight acknowledged', category: 'notes' },
  'insight.snoozed': { label: 'Insight snoozed', category: 'notes' },

  // ---- People, access, permissions ---------------------------------------
  'internal_contact.created': { label: 'Contact created', category: 'people' },
  'internal_contact.updated': { label: 'Contact updated', category: 'people' },
  'internal_contact.inactivated': { label: 'Contact inactivated', category: 'people' },
  /**
   * An export IS audited, where a search deliberately is not (docs/14 Q-2).
   *
   * The rule docs/14 Q-2 settled on for search — "looking something up is not
   * an act" — does not reach this. An export takes data OUT of every guard the
   * app has and puts it in a file somebody will email: it is the one read whose
   * consequences outlive the session. If a vendor list turns up somewhere it
   * should not be, the only question anybody will ask is who pulled it and
   * when, and a ledger that cannot answer that is not a ledger.
   *
   * The event records the LIST and the filters, not the rows: the rows are
   * reconstructible from the filters, and copying them into the ledger would
   * duplicate the whole export inside the audit trail.
   */
  'data.exported': { label: 'List exported', category: 'access' },
  'access.requested': { label: 'Access requested', category: 'access' },
  'access.approved': { label: 'Access approved', category: 'access' },
  'access.rejected': { label: 'Access rejected', category: 'access' },
  'user.signed_in_first': { label: 'First sign-in', category: 'access' },
  'user.profile_synced': { label: 'Profile synced from Entra', category: 'access' },
  'user.role_changed': { label: 'Role changed', category: 'access' },
  'user.disabled': { label: 'User disabled', category: 'access' },
  'user.enabled': { label: 'User reinstated', category: 'access' },
  'addon.changed': { label: 'Permission add-ons changed', category: 'access' },
  'role.permissions_changed': { label: 'Permission set changed', category: 'access' },
  'role.created': { label: 'Role created', category: 'access' },
  'role.updated': { label: 'Role renamed', category: 'access' },
  'role.inactivated': { label: 'Role retired', category: 'access' },
  'role.reactivated': { label: 'Role restored', category: 'access' },
  'assignment.changed': { label: 'Property assignments changed', category: 'access' },
  // These land on the TARGET's history, which is where someone asking "who has
  // been looking at my account" will actually look.
  'impersonation.started': { label: 'Viewed as this person', category: 'access' },
  'impersonation.stopped': { label: 'Stopped viewing as this person', category: 'access' },

  // ---- Security codes -----------------------------------------------------
  'security_code.created': { label: 'Code added', category: 'security' },
  'security_code.changed': { label: 'Code changed', category: 'security' },
  'security_code.inactivated': { label: 'Code retired', category: 'security' },
  'security_code.revealed': { label: 'Code revealed', category: 'security' },
  // The two maintenance CLIs (0107; ADR-78; plans/PKG-A.md A1). Written only by
  // apps/api/src/ops/reset/reset-environment.ts, on the server, over SSH. The
  // reset row is the FIRST row of the ledger it has just emptied, so it names
  // who emptied it, and the flip is the moment resets stopped being possible.
  'environment.reset': { label: 'Environment reset for testing', category: 'security' },
  'launch_state.flipped': { label: 'Environment marked as launched', category: 'security' },

  // ---- The Dev-only QA panel (0108, L-36) ------------------------------------
  // Written only on test: the API refuses /api/v1/qa/* elsewhere, and only the
  // test-only loader writes the checklist.
  'qa_feedback.created': { label: 'QA feedback added', category: 'record' },
  'qa_feedback.updated': { label: 'QA feedback changed', category: 'record' },
  'qa_checklist.loaded': { label: 'QA checklist loaded', category: 'reference' },

  // ---- Reference data -----------------------------------------------------
  'reference_data.created': { label: 'Reference value added', category: 'reference' },
  'reference_data.updated': { label: 'Reference value renamed', category: 'reference' },
  'reference_data.deactivated': { label: 'Reference value deactivated', category: 'reference' },
} as const satisfies Record<string, AuditActionDefinition>;

export type AuditAction = keyof typeof AUDIT_ACTIONS;

/**
 * What to SHOW for an action. Falls back to the raw key rather than throwing —
 * a history tab must render even if a hotfix ships an action the gate would
 * have caught, and the raw key is at least honest about being unknown.
 */
export function auditActionLabel(action: string): string {
  return (
    (AUDIT_ACTIONS as Record<string, AuditActionDefinition | undefined>)[action]?.label ?? action
  );
}

/** Every action in one category — what a category filter expands to. */
export function actionsInCategory(category: AuditCategory): string[] {
  return Object.entries(AUDIT_ACTIONS)
    .filter(([, definition]) => definition.category === category)
    .map(([action]) => action);
}

/**
 * The reader-facing name for a record KIND, as it appears in the ledger's
 * `entity_type` (docs/02's UI-word map is the authority for the wording).
 *
 * Added for the history feed's "Where" filter: a parent's history includes its
 * children's, so the reader has to be able to say "just the agreements". The map
 * is deliberately partial — the ledger outlives the code, and an `entity_type`
 * nobody has labelled yet should render as a humanised key rather than blank.
 */
const ENTITY_KIND_LABELS: Record<string, string> = {
  property: 'Property',
  property_group: 'Property group',
  space: 'Suite',
  tenant_org: 'Tenant',
  tenancy: 'Tenant at a property',
  lease: 'Lease term',
  tenant_contact: 'Tenant contact',
  vendor: 'Vendor',
  vendor_contact: 'Vendor contact',
  vendor_property: 'Vendor–property link',
  agreement: 'Vendor agreement',
  management_agreement: 'Management agreement',
  utility_account: 'Utility account',
  document: 'Document',
  internal_contact: 'Contact',
  key_date: 'Action item',
  note: 'Note',
  security_code: 'Code',
  user: 'User',
  role: 'Role',
  environment: 'Environment',
  launch_state: 'Launch state',
  qa_feedback: 'QA feedback',
  qa_checklist: 'QA checklist',
};

/**
 * `entity_type` → the words a reader recognises. Falls back through the ONE
 * shared rule for an unlabelled internal value (`readableFallback`), which is
 * legible ("purchase_order" → "Purchase order") and is the honest answer for a
 * kind this map has not been taught. The fallback body used to be written out
 * here; punch 5.3 made it shared, because four surfaces had four versions of it
 * and one of them opened only the first underscore.
 */
export function entityKindLabel(entityType: string): string {
  const known = ENTITY_KIND_LABELS[entityType];
  if (known !== undefined) return known;
  return readableFallback(entityType, 'Record');
}
